import { useState } from "react";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

type Props = {
  guideSlug: string;
  initialCount: number;
  initialLiked: boolean;
};

export default function LikeButton({ guideSlug, initialCount, initialLiked }: Props) {
  const theme = usePalette();
  const [count, setCount] = useState(initialCount);
  const [liked, setLiked] = useState(initialLiked);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    const wasLiked = liked;
    // optimistic
    setLiked(!wasLiked);
    setCount((c) => c + (wasLiked ? -1 : 1));
    try {
      if (wasLiked) {
        const r = await api.del<{ likeCount: number; likedByViewer: boolean }>(
          `/api/guides/${guideSlug}/like`,
        );
        setCount(r.likeCount);
        setLiked(r.likedByViewer);
      } else {
        const r = await api.post<{ likeCount: number; likedByViewer: boolean }>(
          `/api/guides/${guideSlug}/like`,
        );
        setCount(r.likeCount);
        setLiked(r.likedByViewer);
      }
    } catch (err) {
      // revert
      setLiked(wasLiked);
      setCount((c) => c + (wasLiked ? 1 : -1));
      if (!(err instanceof ApiError)) {
        console.error(err);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={liked}
      className="inline-flex items-center gap-2 rounded-full px-4 py-2 transition-all hover:shadow-md active:scale-[0.98] disabled:opacity-60"
      style={{
        backgroundColor: liked ? theme.accent : colors.cream,
        color: liked ? "white" : colors.navy,
        border: `1px solid ${liked ? theme.accent : `${colors.tan}55`}`,
        fontFamily: fonts.body,
        fontWeight: 700,
        letterSpacing: "0.04em",
      }}
    >
      <motion.span
        animate={liked ? { scale: [1, 1.3, 1] } : { scale: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="flex items-center"
      >
        <Heart
          size={16}
          fill={liked ? "currentColor" : "none"}
        />
      </motion.span>
      <span className="text-sm">{count}</span>
      <span className="text-[10px] uppercase tracking-[0.12em]" style={{ fontWeight: 700 }}>
        {liked ? "Loved" : "Love this"}
      </span>
    </button>
  );
}
